import React, {useMemo} from 'react';
import {useEnvironment} from '@wix/yoshi-flow-editor';
import {TPAComponentsProvider, Accordion, Text, TextTypography} from '@wix/wix-ui-tpa/cssVars';
import {pageTheme} from '../../../common/components/WithThemeOverride/pageTheme';
import {getBrandStyle} from '../../../common/components/WithThemeOverride/brandStyle';
import {Header} from '../Header/Header';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';
import {usePoliciesControllerProps} from '../Widget/PoliciesControllerContext';
import {classes} from './PoliciesPage.st.css';
import {getDirValue} from '../../../domain/utils/getDirValue';
import {SanitizeInnerHTMLWrapper} from '@wix/ecom-platform-storefront-common';
import {LoadingStateEcommerceSettingsModel} from '../../../domain/models/checkout/LoadingStateEcommerceSettings.model';
import {HeaderLoader} from '../../../common/components/Layout/HeaderLoader/HeaderLoader';
import {PoliciesPageMainLoader} from './PoliciesPageMainLoader';

export enum PoliciesPageDataHook {
  root = 'PoliciesPageDataHook.root',
  header = 'PoliciesPageDataHook.header',
  main = 'PoliciesPageDataHook.main',
  accordion = 'PoliciesPageDataHook.accordion',
}

const getPolicies = (
  loadingStateEcommerceSettings: LoadingStateEcommerceSettingsModel,
  localeKeys: ReturnType<typeof useLocaleKeys>
) => {
  return [
    {
      id: 'termsAndConditions',
      enabled: loadingStateEcommerceSettings?.termsAndConditions?.enabled,
      title: localeKeys.checkout.terms_and_condition_modal.title(),
      content: loadingStateEcommerceSettings?.termsAndConditions?.value,
    },
    {
      id: 'privacyPolicy',
      enabled: loadingStateEcommerceSettings?.privacyPolicy?.enabled,
      title: localeKeys.checkout.privacy_policy_modal.title(),
      content: loadingStateEcommerceSettings?.privacyPolicy?.value,
    },
    {
      id: 'returnPolicy',
      enabled: loadingStateEcommerceSettings?.returnPolicy?.enabled,
      title: localeKeys.checkout.refund_cancellation_modal.title(),
      content: loadingStateEcommerceSettings?.returnPolicy?.value,
    },
    {
      id: 'digitalPolicy',
      enabled: loadingStateEcommerceSettings?.digitalPolicy?.enabled,
      title: localeKeys.checkout.digital_item_policy_modal.title(),
      content: loadingStateEcommerceSettings?.digitalPolicy?.value,
    },
    {
      id: 'customPolicy',
      enabled: loadingStateEcommerceSettings?.customPolicy?.enabled,
      title: loadingStateEcommerceSettings?.customPolicy?.title,
      content: loadingStateEcommerceSettings?.customPolicy?.value,
    },
  ].filter((policy) => policy.enabled);
};

const PoliciesPage = () => {
  const {isRTL, isMobile} = useEnvironment();
  const {checkoutSettingsStore, focusedPolicyId, isLoading} = usePoliciesControllerProps();
  const checkoutBrand = checkoutSettingsStore?.loadingStateEcommerceSettings?.checkoutBrand;
  const rootStyle = {
    ...pageTheme,
    ...getBrandStyle(checkoutBrand),
  };

  const localKeys = useLocaleKeys();
  const policiesTitle = localKeys.checkout.policies.header();
  const policies = getPolicies(checkoutSettingsStore?.loadingStateEcommerceSettings, localKeys);

  const initiallyExpanded = useMemo(() => {
    if (focusedPolicyId) {
      const policyExists = policies.some((policy) => policy.id === focusedPolicyId);
      if (policyExists) {
        return focusedPolicyId;
      }
    }
    return policies[0]?.id;
  }, [focusedPolicyId, policies]);

  return (
    <div style={rootStyle} className={isMobile ? 'mobile-view' : undefined}>
      <TPAComponentsProvider value={{rtl: isRTL}}>
        <div data-hook={PoliciesPageDataHook.root} className={classes.root} dir={getDirValue(isRTL)}>
          {isLoading ? (
            <HeaderLoader />
          ) : (
            <Header
              dataHook={PoliciesPageDataHook.header}
              titleSuffixOverride={policiesTitle}
              ecommerceSettings={checkoutSettingsStore?.ecommerceSettings}
              checkoutBrand={checkoutBrand}
            />
          )}
          <main data-hook={PoliciesPageDataHook.main} className={classes.main}>
            {isLoading ? (
              <PoliciesPageMainLoader />
            ) : (
              policies.length > 0 && (
                <Accordion data-hook={PoliciesPageDataHook.accordion} initiallyExpanded={initiallyExpanded}>
                  {policies.map((policy) => (
                    <Accordion.Item key={policy.id} id={policy.id} title={policy.title}>
                      <Text typography={TextTypography.runningText} className={classes.policyContent}>
                        <SanitizeInnerHTMLWrapper innerHtml={policy.content}>
                          <div />
                        </SanitizeInnerHTMLWrapper>
                      </Text>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )
            )}
          </main>
        </div>
      </TPAComponentsProvider>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default PoliciesPage;
